import {
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
	type Ref,
	type WatchSource,
} from 'vue';

export function useEllipsisDetection(
	watchSource?: WatchSource<unknown> | WatchSource<unknown>[]
) {
	const elementRef: Ref<HTMLElement | null> = ref(null);
	const isEllipsed = ref(false);
	let resizeObserver: ResizeObserver | null = null;

	const updateEllipsisState = () => {
		const element = elementRef.value;
		if (!element) {
			isEllipsed.value = false;
			return;
		}

		isEllipsed.value = element.scrollWidth > element.clientWidth + 1;
	};

	const observeElement = () => {
		if (!resizeObserver) {
			return;
		}
		resizeObserver.disconnect();
		if (elementRef.value) {
			resizeObserver.observe(elementRef.value);
		}
	};

	onMounted(() => {
		void nextTick(() => {
			updateEllipsisState();
			observeElement();
		});

		if (typeof ResizeObserver !== 'undefined') {
			resizeObserver = new ResizeObserver(updateEllipsisState);
			observeElement();
		}

		window.addEventListener('resize', updateEllipsisState);
	});

	onBeforeUnmount(() => {
		resizeObserver?.disconnect();
		resizeObserver = null;
		window.removeEventListener('resize', updateEllipsisState);
	});

	if (watchSource) {
		watch(watchSource, () => {
			void nextTick(() => {
				updateEllipsisState();
				observeElement();
			});
		});
	}

	return {
		elementRef,
		isEllipsed,
		updateEllipsisState,
	};
}
